/**
 * useStockRegistries - Cadastros base do Estoque: endereços e modelos de rastreador
 *
 * 100% via RPCs (`get_stock_locations`, `upsert_stock_location`,
 * `get_tracker_models`, `upsert_tracker_model`, `get_stock_manufacturers`).
 *
 * RELEASE: 2026-10-06 v1.1.0 - Tecnologias de conectividade por modelo.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (Fase 2 do Estoque).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useStockRegistries');

/** Tipos de endereço (espelha a allowlist do banco). */
export const STOCK_LOCATION_TYPES = [
  { value: 'depot', label: 'Depósito' },
  { value: 'store', label: 'Loja' },
  { value: 'office', label: 'Escritório' },
  { value: 'customer_home', label: 'Casa do Cliente' },
] as const;
export type StockLocationType = (typeof STOCK_LOCATION_TYPES)[number]['value'];
export const stockLocationTypeLabel = (v: string) => STOCK_LOCATION_TYPES.find((t) => t.value === v)?.label ?? v;

export interface StockLocation {
  id: string;
  name: string;
  location_type: StockLocationType;
  cep: string | null;
  street: string | null;
  address_number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  notes: string | null;
  is_active: boolean;
  /** Endereço padrão do tenant (único; usado na montagem de kit — BR-EST-024/026). */
  is_default: boolean;
}

export interface TrackerModel {
  id: string;
  name: string;
  notes: string | null;
  is_active: boolean;
  manufacturer_person_id: string | null;
  manufacturer_name: string | null;
  connectivity_technologies: TrackerConnectivityTechnology[];
}

/** Tecnologias fixas aceitas pelo cadastro e pela RPC. */
export const TRACKER_CONNECTIVITY_TECHNOLOGIES = [
  { value: '2g', label: '2G' },
  { value: '4g', label: '4G' },
  { value: 'rf', label: 'RF' },
  { value: 'bluetooth', label: 'Bluetooth' },
  { value: 'satellite', label: 'Satelital' },
] as const;

export type TrackerConnectivityTechnology = (typeof TRACKER_CONNECTIVITY_TECHNOLOGIES)[number]['value'];

/** Traduz o valor persistido sem espalhar rótulos pela interface. */
export const trackerConnectivityLabel = (value: string) =>
  TRACKER_CONNECTIVITY_TECHNOLOGIES.find((technology) => technology.value === value)?.label ?? value;

export interface StockListFilters {
  search: string;
  status: 'active' | 'inactive' | 'all';
  page: number;
  pageSize: number;
  /** Só endereços */
  type?: StockLocationType | 'all';
}

// Mantém o `this` do cliente ao chamar RPCs fora da tipagem gerada
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

/** Lista paginada de endereços de estoque. */
export function useStockLocations(filters: StockListFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-locations', tenantId, filters],
    enabled: !!tenantId,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_locations', {
        p_search: filters.search || null,
        p_type: !filters.type || filters.type === 'all' ? null : filters.type,
        p_status: filters.status,
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar endereços de estoque', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: StockLocation[] };
      return { total: p.total ?? 0, items: p.items ?? [] };
    },
  });
}

/**
 * Endereço de estoque padrão do tenant (BR-EST-026).
 * Reaproveita `get_stock_locations` v1.1.0, que devolve o padrão primeiro.
 */
export function useDefaultStockLocation(enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-locations', tenantId, 'default'],
    enabled: !!tenantId && enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_locations', {
        p_status: 'active', p_page: 1, p_page_size: 1, p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao buscar endereço padrão', { error: error.message });
        throw new Error(error.message);
      }
      const first = ((data || {}) as { items?: StockLocation[] }).items?.[0];
      return first?.is_default ? first : null;
    },
  });
}

/** Lista paginada de modelos de rastreador. */
export function useTrackerModels(filters: StockListFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['tracker-models', tenantId, filters],
    enabled: !!tenantId,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_tracker_models', {
        p_search: filters.search || null,
        p_status: filters.status,
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar modelos de rastreador', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: TrackerModel[] };
      return { total: p.total ?? 0, items: p.items ?? [] };
    },
  });
}

/** Pessoas com papel Fabricante (seletor do modelo). */
export function useStockManufacturers(search = '', enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-manufacturers', tenantId, search],
    enabled: !!tenantId && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_manufacturers', {
        p_search: search || null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
      return (data as { id: string; name: string }[]) ?? [];
    },
  });
}

/** Mutações dos cadastros base. */
export function useStockRegistryMutations() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();

  const upsertLocation = useMutation({
    mutationFn: async ({ id, data }: { id: string | null; data: Record<string, unknown> }) => {
      const { error } = await rpc('upsert_stock_location', { p_data: data, p_location_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-locations', tenantId] });
      toast.success('Endereço salvo');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const upsertModel = useMutation({
    mutationFn: async ({ id, data }: { id: string | null; data: Record<string, unknown> }) => {
      const { data: savedId, error } = await rpc('upsert_tracker_model', { p_data: data, p_model_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      // 2026-10-08: devolve o id para o atalho "+" selecionar o modelo criado
      return savedId as string | null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tracker-models', tenantId] });
      toast.success('Modelo salvo');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // 2026-10-07: planos de dados dos chips
  const upsertDataPlan = useMutation({
    mutationFn: async ({ id, data }: { id: string | null; data: Record<string, unknown> }) => {
      const { data: savedId, error } = await rpc('upsert_stock_data_plan', { p_data: data, p_plan_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      // Devolve o id para o atalho do chip já selecionar o plano novo
      return savedId as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-data-plans', tenantId] });
      qc.invalidateQueries({ queryKey: ['stock-items'] });
      toast.success('Plano de dados salvo');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return { upsertLocation, upsertModel, upsertDataPlan };
}

// =============================================================== Planos de dados

/** Operadoras aceitas (espelha a allowlist de `upsert_stock_data_plan`). */
export const DATA_PLAN_CARRIERS = [
  { value: 'vivo', label: 'Vivo' },
  { value: 'tim', label: 'Tim' },
  { value: 'claro', label: 'Claro' },
  { value: 'oi', label: 'Oi' },
  { value: 'algar', label: 'Algar' },
  { value: 'nextel', label: 'Nextel' },
] as const;
export type DataPlanCarrier = (typeof DATA_PLAN_CARRIERS)[number]['value'];
export const dataPlanCarrierLabel = (v: string) => DATA_PLAN_CARRIERS.find((c) => c.value === v)?.label ?? v;

export interface StockDataPlan {
  id: string;
  name: string;
  supplier_person_id: string | null;
  supplier_name: string | null;
  /** v1.1.0 (2026-10-08): pacote em MB (inteiro) */
  data_package_mb: number | null;
  loyalty_months: number | null;
  grace_days: number | null;
  compatible_carriers: DataPlanCarrier[];
  original_amount: number | null;
  negotiated_amount: number | null;
  due_day: number | null;
  closing_day: number | null;
  penalty_per_chip: number | null;
  overage_mb_amount: number | null;
  overage_gb_amount: number | null;
  sms_amount: number | null;
  notes: string | null;
  is_active: boolean;
}

/** Lista paginada de planos de dados (também alimenta o seletor do chip). */
export function useStockDataPlans(filters: StockListFilters, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-data-plans', tenantId, filters],
    enabled: !!tenantId && enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_data_plans', {
        p_search: filters.search || null,
        p_status: filters.status,
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar planos de dados', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: StockDataPlan[] };
      return { total: p.total ?? 0, items: p.items ?? [] };
    },
  });
}

/** Detalhe de um plano (modal de visualização na lista de chips). */
export function useStockDataPlanDetail(planId: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-data-plan-detail', tenantId, planId],
    enabled: !!tenantId && !!planId,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_data_plan_detail', { p_plan_id: planId, p_tenant_hint: tenantId ?? null });
      if (error) {
        logger.error('Erro ao carregar plano de dados', { error: error.message });
        throw new Error(error.message);
      }
      return data as StockDataPlan;
    },
  });
}

/** Formata o pacote em MB para exibição. */
export const formatPackageMb = (mb: number | null | undefined) =>
  mb === null || mb === undefined ? '—' : `${mb.toLocaleString('pt-BR')} MB`;
